import { useCallback, useEffect, useState } from 'react';

export type Theme = 'clair' | 'sombre' | 'auto';

const CLE = 'repere.theme';

const LIBELLES: Record<Theme, string> = {
  clair: 'Clair',
  sombre: 'Sombre',
  auto: 'Auto',
};

/**
 * Applique le thème au document.
 *
 * `auto` **retire** l'attribut au lieu d'en poser un troisième : c'est l'absence de choix
 * explicite qui laisse `prefers-color-scheme` décider, et la feuille de style est écrite
 * autour de cette absence (voir `cssVariables` dans les tokens). Poser `data-theme="auto"`
 * ne correspondrait à aucune règle et figerait l'interface en clair.
 */
export function appliquerTheme(theme: Theme): void {
  const racine = document.documentElement;
  if (theme === 'auto') racine.removeAttribute('data-theme');
  else racine.setAttribute('data-theme', theme === 'sombre' ? 'dark' : 'light');
}

/** Lit le choix conservé. `auto` par défaut : on suit l'appareil tant qu'on n'a rien dit. */
export function themeConserve(): Theme {
  try {
    const valeur = localStorage.getItem(CLE);
    return valeur === 'clair' || valeur === 'sombre' ? valeur : 'auto';
  } catch {
    // Navigation privée, stockage refusé : on suit l'appareil, sans faire d'histoire.
    return 'auto';
  }
}

/**
 * Choix du thème — clair, sombre, ou celui de l'appareil.
 *
 * Trois boutons plutôt qu'une bascule à deux états : une bascule ne sait pas dire « suis
 * mon téléphone », et c'est pourtant le réglage que la plupart des gens veulent — leur
 * appareil bascule seul au coucher du soleil, l'application doit suivre sans qu'on y pense.
 *
 * `aria-pressed` et non un groupe de boutons radio : ce sont trois actions immédiates, pas
 * un formulaire à valider. Le lecteur d'écran annonce « Sombre, activé », ce qui dit à la
 * fois ce que fait le bouton et où l'on en est.
 */
export function ChoixTheme() {
  const [theme, setTheme] = useState<Theme>(() => themeConserve());

  // Le thème est déjà posé par le script d'amorçage de `index.html` — cet effet ne rattrape
  // que les changements faits ici, et la première synchronisation après un rendu serveur
  // ou un test qui monte le composant sans passer par la page.
  useEffect(() => {
    appliquerTheme(theme);
  }, [theme]);

  const choisir = useCallback((choix: Theme) => {
    setTheme(choix);
    try {
      if (choix === 'auto') localStorage.removeItem(CLE);
      else localStorage.setItem(CLE, choix);
    } catch {
      // Le choix vaut alors pour la session en cours seulement : mieux que de refuser.
    }
  }, []);

  return (
    <div className="choix-theme" role="group" aria-label="Apparence">
      {(Object.keys(LIBELLES) as Theme[]).map((valeur) => (
        <button
          key={valeur}
          type="button"
          className={valeur === theme ? 'choix-theme-option actif' : 'choix-theme-option'}
          aria-pressed={valeur === theme}
          onClick={() => choisir(valeur)}
        >
          {LIBELLES[valeur]}
        </button>
      ))}
    </div>
  );
}
